export interface ErroApi {
  erro: string;
  detalhes?: unknown;
}

export class FalhaApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: unknown,
    // true quando a resposta não veio com um corpo JSON `{erro: "..."}` reconhecível — ou seja, o
    // servidor da aplicação nem chegou a avaliar a requisição (502/503 de um proxy com o backend
    // fora do ar, página de erro HTML de um gateway, etc.). Diferente de uma recusa real da
    // aplicação (400/404/409 com corpo JSON válido), que significa "o servidor respondeu e
    // recusou" — isso aqui significa "não dá pra saber se chegou a ser avaliado". Fase 6 (fila
    // offline) usa esse sinal pra tratar como falha de rede em vez de rejeição definitiva: achado
    // testando de verdade — o proxy do Vite em dev (e o Traefik em produção, mesmo padrão do bug
    // de upload de foto corrigido em 11/08) devolve uma resposta HTTP de erro normal quando o
    // backend está fora do ar, não uma exceção de rede crua, então sem esse sinal a fila offline
    // descartava mutações reais depois de só 3 tentativas contra um servidor que nunca respondeu.
    readonly corpoInvalido: boolean = false
  ) {
    super(mensagem);
    this.name = "FalhaApi";
  }
}

async function requisitar<T>(caminho: string, init?: RequestInit): Promise<T> {
  // FormData (upload de foto) não pode levar Content-Type manual: o browser precisa gerar o
  // boundary do multipart sozinho, e um header fixo aqui quebra o parsing no servidor.
  const corpoFormData = init?.body instanceof FormData;
  const resposta = await fetch(`/api${caminho}`, {
    ...init,
    headers: {
      ...(init?.body && !corpoFormData ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
    // O cookie de sessão é httpOnly; "same-origin" garante que ele acompanhe a requisição.
    credentials: "same-origin",
  });

  if (resposta.status === 204) return undefined as T;

  const corpo = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    const corpoValido = corpo !== null && typeof corpo === "object" && "erro" in corpo;
    const erro = corpoValido ? (corpo as ErroApi).erro : "Falha na comunicação com o servidor";
    throw new FalhaApi(resposta.status, erro, (corpo as ErroApi | null)?.detalhes, !corpoValido);
  }

  return corpo as T;
}

export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  papel: "SUPER_ADMIN" | "ADMIN" | "VISTORIADOR";
  organizacaoId: string;
  organizacaoNome: string;
}

export interface ResponsavelApi {
  id: string;
  nome: string;
  papel: string | null;
  email: string | null;
  telefone: string | null;
  recebeRelatorio: boolean;
}

// Cliente = o prédio a vistoriar (fusão Cliente+Predio, ver server/prisma/schema.prisma).
// administradora é texto livre, só quando o prédio de fato pertence a uma gestora terceira.
export interface ClienteApi {
  id: string;
  nome: string;
  administradora: string | null;
  cnpj: string | null;
  emailContato: string | null;
  telefoneContato: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  responsaveis: ResponsavelApi[];
}

export interface DadosCliente {
  nome: string;
  administradora?: string;
  cnpj?: string;
  emailContato?: string;
  telefoneContato?: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
}

export interface DadosResponsavel {
  nome: string;
  papel?: string;
  email?: string;
  telefone?: string;
  recebeRelatorio?: boolean;
}

export type StatusTemplate = "RASCUNHO" | "PUBLICADO" | "ARQUIVADO";
export type TipoItemTemplate = "CHECKBOX_SIMPLES" | "STATUS_MULTIPLO" | "TEXTO_LIVRE" | "SIM_NAO" | "CONFORME";
export interface AvaliacaoItemApi { modo: "PADRAO" | "OPCOES" | "MARCACAO_MANUAL"; valoresProblema?: string[]; }

export interface TemplateItemApi {
  id: string;
  chave: string;
  rotulo: string;
  tipo: TipoItemTemplate;
  opcoes: string[] | null;
  avaliacao: AvaliacaoItemApi | null;
  ordem: number;
}

export interface TemplateSecaoApi {
  id: string;
  chave: string;
  titulo: string;
  ordem: number;
  itens: TemplateItemApi[];
}

export interface TemplateApi {
  id: string;
  nome: string;
  tipo: string;
  versao: number;
  status: StatusTemplate;
  criadoEm: string;
  secoes: TemplateSecaoApi[];
}

export interface TemplateResumoApi {
  id: string;
  nome: string;
  tipo: string;
  versao: number;
  status: StatusTemplate;
  criadoEm: string;
  _count: { secoes: number };
}

export interface DadosSecao {
  chave?: string;
  titulo: string;
}

export interface DadosItem {
  chave?: string;
  rotulo: string;
  tipo: TipoItemTemplate;
  opcoes?: string[];
  avaliacao?: AvaliacaoItemApi | null;
}

export interface UsuarioResumoApi {
  id: string;
  nome: string;
  email: string;
  papel: "ADMIN" | "VISTORIADOR";
  ativo: boolean;
  ultimoLoginEm: string | null;
}

export interface DadosCriarUsuario {
  nome: string;
  email: string;
  papel: "ADMIN" | "VISTORIADOR";
  senha: string;
}

export interface DadosEditarUsuario {
  nome?: string;
  email?: string;
  papel?: "ADMIN" | "VISTORIADOR";
  ativo?: boolean;
}

export interface ResumoPlataformaApi {
  totais: { organizacoes: number; usuarios: number; clientes: number; vistorias: number };
  organizacoes: { id: string; nome: string; status: string; criadaEm: string; _count: { usuarios: number; clientes: number; vistorias: number } }[];
  clientes: { id: string; nome: string; ativo: boolean; organizacao: { nome: string } }[];
}

export interface DadosCriarOrganizacaoPlataforma { nome: string; adminNome: string; adminEmail: string; }
export interface EmpresaApi { id: string; nome: string; cnpj: string | null; email: string | null; telefone: string | null; endereco: string | null; cidade: string | null; estado: string | null; site: string | null; logoPath: string | null; }

export type StatusAgendamento = "AGENDADO" | "CONFIRMADO" | "REALIZADO" | "CANCELADO";

export interface AgendamentoApi {
  id: string;
  clienteId: string;
  cliente: { id: string; nome: string };
  vistoriadorId: string;
  vistoriador: { id: string; nome: string };
  templateId: string | null;
  dataHora: string;
  duracaoMinutos: number | null;
  status: StatusAgendamento;
  observacoes: string | null;
  // Preenchido quando a vistoria é criada a partir deste agendamento (status vira REALIZADO) —
  // usado pra abrir a vistoria ao clicar num agendamento já realizado, em vez de tentar cancelar.
  vistoriaId: string | null;
}

export interface DadosAgendamento {
  clienteId: string;
  vistoriadorId: string;
  dataHora: string;
  duracaoMinutos?: number;
  observacoes?: string;
  templateId?: string;
}

export type StatusVistoria = "EM_ANDAMENTO" | "CONCLUIDA" | "PDF_GERADO" | "ENVIADA";

export interface VistoriaResumoApi {
  id: string;
  cliente: { id: string; nome: string };
  atribuidoA: { id: string; nome: string };
  dataVistoria: string;
  status: StatusVistoria;
  excluidaEm: string | null;
  excluidaPor: { id: string; nome: string } | null;
}

export interface SnapshotItem {
  chave: string;
  rotulo: string;
  tipo: TipoItemTemplate;
  opcoes?: string[];
  avaliacao?: AvaliacaoItemApi | null;
}

export interface SnapshotSecao {
  chave: string;
  titulo: string;
  itens: SnapshotItem[];
}

export interface TemplateSnapshot {
  nome: string;
  tipo: string;
  secoes: SnapshotSecao[];
}

export interface VistoriaRespostaApi {
  id: string;
  secaoChave: string;
  itemChave: string;
  marcado: boolean | null;
  valorEscolhido: string | null;
  descricaoNaoConformidade: string | null;
  problemaIdentificado: boolean | null;
}

export interface VistoriaObservacaoApi {
  id: string;
  secaoChave: string;
  texto: string;
}

// Achado "fora do checklist" — ver comentário em schema.prisma (NaoConformidade.avulso). Só os
// campos que a tela de vistoria precisa pra renderizar o card; o resto (status, tarefa) é
// administrado nas telas de Tarefas/Pendências, não aqui.
export interface AchadoAvulsoApi {
  id: string;
  secaoChave: string;
  itemChave: string;
  descricao: string | null;
}

export interface FotoApi {
  id: string;
  secaoChave: string;
  itemChave: string | null;
  legenda: string | null;
  capturadaEmDispositivoEm: string | null;
  criadoEm: string;
}

export interface VistoriaApi {
  id: string;
  cliente: { id: string; nome: string; administradora: string | null };
  atribuidoA: { id: string; nome: string };
  agendamentoId: string | null;
  templateSnapshot: TemplateSnapshot;
  dataVistoria: string;
  horarioInicio: string | null;
  horarioTermino: string | null;
  responsavelNome: string | null;
  irregularidades: string | null;
  providencias: { opcoes: string[]; descricao?: string } | null;
  assinaturaResponsavelNome: string | null;
  status: StatusVistoria;
  pdfPath: string | null;
  excluidaEm: string | null;
  excluidaPor: { id: string; nome: string } | null;
  respostas: VistoriaRespostaApi[];
  observacoesSecao: VistoriaObservacaoApi[];
  fotos: FotoApi[];
  achadosAvulsos: AchadoAvulsoApi[];
}

export type StatusNaoConformidade = "ABERTA" | "RESOLVIDA";
export type StatusTarefa = "AGUARDANDO_ATRIBUICAO" | "PENDENTE" | "EM_ANDAMENTO" | "AGUARDANDO_VERIFICACAO" | "CONCLUIDA" | "CANCELADA";
export type PrioridadeTarefa = "BAIXA" | "MEDIA" | "ALTA";

export interface TarefaApi {
  id: string;
  titulo: string;
  descricao: string | null;
  responsavelNome: string | null;
  responsavelUsuarioId: string | null;
  responsavelUsuario: { id: string; nome: string } | null;
  prazo: string | null;
  prioridade: PrioridadeTarefa;
  status: StatusTarefa;
  concluidaEm: string | null;
  evidenciaDescricao: string | null;
}

export interface NaoConformidadeApi {
  id: string;
  cliente: { id: string; nome: string };
  vistoria: { id: string; dataVistoria: string; atribuidoA: { id: string; nome: string } };
  secaoTitulo: string;
  itemRotulo: string;
  descricao: string | null;
  status: StatusNaoConformidade;
  tarefas: TarefaApi[];
  verificacoes: {
    id: string; resultado: "RESOLVIDA" | "PERSISTE"; observacao: string | null; criadaEm: string;
    verificadaPor: { id: string; nome: string }; vistoria: { id: string; dataVistoria: string };
  }[];
}

export interface PendenciaAnteriorApi {
  id: string;
  secaoTitulo: string;
  itemRotulo: string;
  descricao: string | null;
  criadaEm: string;
  vistoria: { id: string; dataVistoria: string };
  tarefas: TarefaApi[];
  verificacoes: { vistoriaId: string; resultado: "RESOLVIDA" | "PERSISTE"; observacao: string | null; criadaEm: string }[];
}

export interface TarefaDetalhadaApi extends TarefaApi {
  naoConformidade: {
    id: string; secaoTitulo: string; itemRotulo: string; descricao: string | null; status: StatusNaoConformidade;
    cliente: { id: string; nome: string }; vistoria: { id: string; dataVistoria: string };
  };
}

export interface DadosCriarVistoria {
  clienteId: string;
  agendamentoId?: string;
  vistoriadorId?: string;
  dataVistoria: string;
  horarioInicio?: string;
  responsavelNome?: string;
}

export interface DadosEditarVistoria {
  horarioInicio?: string;
  responsavelNome?: string;
  irregularidades?: string;
  providencias?: { opcoes: string[]; descricao?: string };
  assinaturaResponsavelNome?: string;
}

export const api = {
  login: (email: string, senha: string) =>
    requisitar<{ usuario: UsuarioSessao }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, senha }),
    }),

  logout: () => requisitar<void>("/auth/logout", { method: "POST" }),

  eu: () => requisitar<{ usuario: UsuarioSessao }>("/auth/eu"),

  resumoPlataforma: () => requisitar<ResumoPlataformaApi>("/plataforma/resumo"),

  criarOrganizacaoPlataforma: (dados: DadosCriarOrganizacaoPlataforma) => requisitar<{ organizacao: { id: string; nome: string }; administrador: { id: string; nome: string; email: string }; senhaInicial: string }>("/plataforma/organizacoes", { method: "POST", body: JSON.stringify(dados) }),

  editarStatusOrganizacao: (id: string, status: "ativo" | "inativo") => requisitar<void>(`/plataforma/organizacoes/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  obterEmpresa: () => requisitar<{ empresa: EmpresaApi }>("/empresa"),
  editarEmpresa: (dados: Partial<EmpresaApi>) => requisitar<{ empresa: EmpresaApi }>("/empresa", { method: "PATCH", body: JSON.stringify(dados) }),
  enviarLogoEmpresa: (arquivo: File) => { const dados = new FormData(); dados.append("logo", arquivo); return requisitar<{ empresa: EmpresaApi }>("/empresa/logo", { method: "POST", body: dados }); },

  listarClientes: () => requisitar<{ clientes: ClienteApi[] }>("/clientes"),

  criarCliente: (dados: DadosCliente) =>
    requisitar<{ cliente: ClienteApi }>("/clientes", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  editarCliente: (id: string, dados: Partial<DadosCliente>) =>
    requisitar<void>(`/clientes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  excluirCliente: (id: string) => requisitar<void>(`/clientes/${id}`, { method: "DELETE" }),

  criarResponsavel: (clienteId: string, dados: DadosResponsavel) =>
    requisitar<{ responsavel: ResponsavelApi }>(`/clientes/${clienteId}/responsaveis`, {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  excluirResponsavel: (id: string) =>
    requisitar<void>(`/clientes/responsaveis/${id}`, { method: "DELETE" }),

  listarTemplates: () => requisitar<{ templates: TemplateResumoApi[] }>("/templates"),

  criarTemplate: (dados: { nome: string; tipo: string }) =>
    requisitar<{ template: TemplateApi }>("/templates", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  obterTemplate: (id: string) => requisitar<{ template: TemplateApi }>(`/templates/${id}`),

  editarTemplate: (id: string, dados: { nome: string }) => requisitar<{ template: TemplateApi }>(`/templates/${id}`, { method: "PATCH", body: JSON.stringify(dados) }),

  duplicarTemplate: (id: string) =>
    requisitar<{ template: TemplateApi }>(`/templates/${id}/duplicar`, { method: "POST" }),

  novaVersaoTemplate: (id: string) =>
    requisitar<{ template: TemplateApi }>(`/templates/${id}/nova-versao`, { method: "POST" }),

  publicarTemplate: (id: string) =>
    requisitar<{ template: TemplateApi }>(`/templates/${id}/publicar`, { method: "POST" }),

  criarSecao: (templateId: string, dados: DadosSecao) =>
    requisitar<{ secao: TemplateSecaoApi }>(`/templates/${templateId}/secoes`, {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  editarSecao: (id: string, dados: Partial<DadosSecao>) =>
    requisitar<{ secao: TemplateSecaoApi }>(`/templates/secoes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  excluirSecao: (id: string) => requisitar<void>(`/templates/secoes/${id}`, { method: "DELETE" }),

  criarItem: (secaoId: string, dados: DadosItem) =>
    requisitar<{ item: TemplateItemApi }>(`/templates/secoes/${secaoId}/itens`, {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  editarItem: (id: string, dados: Partial<DadosItem>) =>
    requisitar<{ item: TemplateItemApi }>(`/templates/itens/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  excluirItem: (id: string) => requisitar<void>(`/templates/itens/${id}`, { method: "DELETE" }),

  listarUsuarios: () => requisitar<{ usuarios: UsuarioResumoApi[] }>("/usuarios"),

  listarAgendamentos: (inicio: string, fim: string) =>
    requisitar<{ agendamentos: AgendamentoApi[] }>(
      `/agendamentos?inicio=${encodeURIComponent(inicio)}&fim=${encodeURIComponent(fim)}`
    ),

  criarAgendamento: (dados: DadosAgendamento) =>
    requisitar<{ agendamento: AgendamentoApi }>("/agendamentos", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  cancelarAgendamento: (id: string) =>
    requisitar<void>(`/agendamentos/${id}/cancelar`, { method: "POST" }),

  listarVistorias: (opcoes?: { excluidas?: boolean }) =>
    requisitar<{ vistorias: VistoriaResumoApi[] }>(`/vistorias${opcoes?.excluidas ? "?excluidas=true" : ""}`),

  criarVistoria: (dados: DadosCriarVistoria) =>
    requisitar<{ vistoria: VistoriaApi }>("/vistorias", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  obterVistoria: (id: string) => requisitar<{ vistoria: VistoriaApi; pendenciasAnteriores: PendenciaAnteriorApi[] }>(`/vistorias/${id}`),

  editarVistoria: (id: string, dados: DadosEditarVistoria) =>
    requisitar<{ vistoria: VistoriaApi }>(`/vistorias/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  salvarResposta: (
    id: string,
    dados: { secaoChave: string; itemChave: string; marcado?: boolean; valorEscolhido?: string; descricao?: string; problema?: boolean }
  ) =>
    requisitar<{ resposta: VistoriaRespostaApi }>(`/vistorias/${id}/respostas`, {
      method: "PUT",
      body: JSON.stringify(dados),
    }),

  salvarObservacao: (id: string, dados: { secaoChave: string; texto: string }) =>
    requisitar<{ observacao: VistoriaObservacaoApi }>(`/vistorias/${id}/observacoes`, {
      method: "PUT",
      body: JSON.stringify(dados),
    }),

  salvarAchadoAvulso: (id: string, dados: { secaoChave: string; itemChave: string; descricao: string }) =>
    requisitar<{ achado: AchadoAvulsoApi }>(`/vistorias/${id}/achados-avulsos`, {
      method: "PUT",
      body: JSON.stringify(dados),
    }),

  excluirAchadoAvulso: (vistoriaId: string, itemChave: string) =>
    requisitar<void>(`/vistorias/${vistoriaId}/achados-avulsos/${itemChave}`, { method: "DELETE" }),

  criarUsuario: (dados: DadosCriarUsuario) =>
    requisitar<{ usuario: UsuarioResumoApi }>("/usuarios", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  editarUsuario: (id: string, dados: DadosEditarUsuario) =>
    requisitar<{ usuario: UsuarioResumoApi }>(`/usuarios/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  redefinirSenhaUsuario: (id: string, senha: string) =>
    requisitar<void>(`/usuarios/${id}/redefinir-senha`, {
      method: "POST", body: JSON.stringify({ senha }),
    }),

  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    requisitar<void>("/auth/trocar-senha", {
      method: "POST", body: JSON.stringify({ senhaAtual, novaSenha }),
    }),

  // `arquivo` é Blob (não File) pra aceitar tanto o <input type="file"> quanto a fila offline
  // (Fase 6) reidratando um Blob salvo em IndexedDB — nenhum dos dois carrega .name garantido, daí
  // nomeArquivo como parâmetro à parte em vez de depender de `arquivo.name`. `mutationId`
  // opcional: quando vem da fila offline, deixa o servidor devolver a mesma foto de uma
  // retentativa em vez de criar outra (ver SyncMutacaoAplicada no schema do servidor).
  uploadFoto: (
    vistoriaId: string,
    arquivo: Blob,
    nomeArquivo: string,
    dados: { secaoChave: string; itemChave?: string; capturadaEmDispositivoEm?: string },
    mutationId?: string
  ) => {
    const form = new FormData();
    form.append("foto", arquivo, nomeArquivo);
    form.append("secaoChave", dados.secaoChave);
    if (dados.itemChave) form.append("itemChave", dados.itemChave);
    if (dados.capturadaEmDispositivoEm) form.append("capturadaEmDispositivoEm", dados.capturadaEmDispositivoEm);
    if (mutationId) form.append("mutationId", mutationId);
    return requisitar<{ foto: FotoApi }>(`/vistorias/${vistoriaId}/fotos`, { method: "POST", body: form });
  },

  editarFoto: (vistoriaId: string, fotoId: string, dados: { legenda: string }) =>
    requisitar<{ foto: FotoApi }>(`/vistorias/${vistoriaId}/fotos/${fotoId}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  excluirFoto: (vistoriaId: string, fotoId: string) =>
    requisitar<void>(`/vistorias/${vistoriaId}/fotos/${fotoId}`, { method: "DELETE" }),

  urlFoto: (vistoriaId: string, fotoId: string, variante?: "miniatura") =>
    `/api/vistorias/${vistoriaId}/fotos/${fotoId}/arquivo${variante ? `?variante=${variante}` : ""}`,

  // Mesma rota do urlFoto, só com ?baixar=1 — o servidor troca sendFile (inline) por download
  // (Content-Disposition: attachment), mesmo padrão do urlPdf/res.download.
  urlFotoDownload: (vistoriaId: string, fotoId: string) =>
    `/api/vistorias/${vistoriaId}/fotos/${fotoId}/arquivo?baixar=1`,

  concluirVistoria: (id: string) =>
    requisitar<{ vistoria: VistoriaApi }>(`/vistorias/${id}/concluir`, { method: "POST" }),

  urlPdf: (vistoriaId: string) => `/api/vistorias/${vistoriaId}/pdf`,
  excluirVistoria: (id: string) => requisitar<void>(`/vistorias/${id}`, { method: "DELETE" }),

  listarNaoConformidades: (status?: StatusNaoConformidade) =>
    requisitar<{ naoConformidades: NaoConformidadeApi[] }>(
      `/nao-conformidades${status ? `?status=${status}` : ""}`
    ),

  editarNaoConformidade: (id: string, status: StatusNaoConformidade) =>
    requisitar<{ naoConformidade: NaoConformidadeApi }>(`/nao-conformidades/${id}`, {
      method: "PATCH", body: JSON.stringify({ status }),
    }),

  criarTarefa: (naoConformidadeId: string, dados: {
    titulo: string; descricao?: string; responsavelNome?: string; responsavelUsuarioId?: string; prazo?: string; prioridade?: PrioridadeTarefa;
  }) => requisitar<{ tarefa: TarefaApi }>(`/nao-conformidades/${naoConformidadeId}/tarefas`, {
    method: "POST", body: JSON.stringify(dados),
  }),

  editarTarefa: (id: string, dados: Partial<{
    titulo: string; descricao: string; responsavelNome: string; prazo: string;
    responsavelUsuarioId: string; prioridade: PrioridadeTarefa; status: StatusTarefa; evidenciaDescricao: string;
  }>) => requisitar<{ tarefa: TarefaApi }>(`/nao-conformidades/tarefas/${id}`, {
    method: "PATCH", body: JSON.stringify(dados),
  }),

  listarTarefas: () => requisitar<{ tarefas: TarefaDetalhadaApi[] }>("/nao-conformidades/tarefas"),

  verificarNaoConformidade: (vistoriaId: string, dados: { naoConformidadeId: string; resultado: "RESOLVIDA" | "PERSISTE"; observacao?: string }) =>
    requisitar<void>(`/vistorias/${vistoriaId}/verificacoes`, { method: "POST", body: JSON.stringify(dados) }),
};
